import { useMemo, useState } from 'react';
import {
  ContextMenuBuilder,
  ContextMenu,
  ErpInputTableV2,
  type DataTableColumn,
  type DataTableRowEventHandler,
  type InputTableCellParams,
  type RepeatRowsPayload
} from '@/components/custom/erp';
import { SelectedCellInfo, useAutoSelectBoPhan, useDetailPaste } from '../hooks';
import { exportPhieuXuatKhoDetailsToExcel } from '../../../utils/detail-export';
import { usePhieuXuatKhoSearchContext } from '../../../context';
import type { DetailRow } from '../hooks/useDetailRows';
import { copyCellValue } from '@/lib/table-handlers';
import { getDetailColumns } from './columns';
import { QUERY_KEYS } from '@/constants';
import { FormMode } from '@/types/form';
import { useCRUD } from '@/hooks';

type RowClickHandler = DataTableRowEventHandler<DetailRow>;
type DetailPasteConfig = Record<string, { resolver: (codes: string[]) => Promise<unknown[]>; resultField?: string }>;

interface DetailTabProps {
  formMode: FormMode;
  rows: DetailRow[];
  documentNumber?: string;
  selectedRowUuid?: string | null;
  selectedCell?: SelectedCellInfo | null;
  onRowClick?: RowClickHandler;
  onCellClick?: (params: InputTableCellParams) => void;
  onAddRow: () => void;
  onDeleteRow: () => void;
  onCopyRow: () => void;
  onPasteRow: (clipboardText?: string) => void | Promise<void>;
  onMoveRow: (direction: 'up' | 'down') => void;
  onCellValueChange: (rowUuid: string | undefined, field: string, newValue: unknown) => void;
  handleUpdateNonWritableFields?: (fields: Set<string>) => void;
  handleUpdatePasteConfigs?: (configs: DetailPasteConfig) => void;
  onRepeatForRows?: (payload: RepeatRowsPayload<DetailRow>) => void;
}

export const DetailTab: React.FC<DetailTabProps> = ({
  formMode,
  rows,
  documentNumber,
  selectedRowUuid,
  selectedCell,
  onCellClick,
  onRowClick,
  onAddRow,
  onDeleteRow,
  onCopyRow,
  onPasteRow,
  onMoveRow,
  onCellValueChange,
  handleUpdateNonWritableFields,
  handleUpdatePasteConfigs,
  onRepeatForRows
}) => {
  const [contextMenu, setContextMenu] = useState<{
    position: { x: number; y: number } | null;
    rowData: DetailRow | null;
    columnField: string | null;
  }>({
    position: null,
    rowData: null,
    columnField: null
  });
  const handleCellValueChange = useAutoSelectBoPhan(onCellValueChange);
  const { hasInvoiceParams } = usePhieuXuatKhoSearchContext();
  const disable = formMode === 'view' || hasInvoiceParams;
  const { fetchDetail } = useCRUD({
    endpoint: QUERY_KEYS.VAT_TU,
    initialFetch: false
  });
  const columns = useMemo(
    () => getDetailColumns(handleCellValueChange, fetchDetail, disable) as DataTableColumn<DetailRow>[],
    [disable, fetchDetail, handleCellValueChange]
  );

  useDetailPaste({
    columns,
    handleUpdateNonWritableFields,
    handleUpdatePasteConfigs
  });

  const contextMenuOptions = useMemo(
    () =>
      ContextMenuBuilder.buildOptions({
        contextMenu,
        columns,
        getRowId: row => row?.uuid || '',
        onCopyData: ({ columnValue, columnField, columnValues }) => {
          copyCellValue({
            columnField,
            columnValue,
            columnValues,
            fallback: text => console.log('Copy data per column:', text)
          });
        },
        onRepeatForRows,
        rowsCount: rows.length
      }),
    [columns, contextMenu, onRepeatForRows, rows.length]
  );

  return (
    <>
      <ErpInputTableV2
        rows={rows}
        autoHeight
        showIndexColumn
        bottomSelector='[data-arito-bottom-bar]'
        bottomOffset={0}
        onRowClick={onRowClick}
        onCellClick={onCellClick}
        onCellContextMenu={(event, params) => {
          onCellClick?.(params);
          const pointerEvent = event as MouseEvent;

          setContextMenu({
            position: { x: pointerEvent.clientX, y: pointerEvent.clientY },
            rowData: params.row,
            columnField: params.field
          });
        }}
        selectedRowId={selectedRowUuid || undefined}
        columns={columns}
        getRowId={row => row?.uuid || ''}
        pasteTarget={selectedCell}
        actionsDisabled={formMode === 'view'}
        actionOrder={['add', 'delete', 'copy', 'paste', 'moveUp', 'moveDown', 'export', 'pin']}
        actions={{
          add: onAddRow,
          delete: onDeleteRow,
          copy: onCopyRow,
          paste: onPasteRow,
          moveUp: () => onMoveRow('up'),
          moveDown: () => onMoveRow('down'),
          export: () => exportPhieuXuatKhoDetailsToExcel(rows, documentNumber),
          pin: () => console.log('Pin clicked')
        }}
      />

      <ContextMenu
        options={contextMenuOptions}
        position={contextMenu.position}
        onClose={() =>
          setContextMenu({
            position: null,
            rowData: null,
            columnField: null
          })
        }
      />
    </>
  );
};
